import * as z from "zod/mini";
import {
  AKAHU_AMOUNT_LIMIT_HELP,
  AKAHU_AMOUNT_OVER_LIMIT,
  AKAHU_METHOD_DESCRIPTION,
  AKAHU_METHOD_INSTRUCTIONS,
  AKAHU_METHOD_NAME,
  AKAHU_ONE_OFF_MAX_AMOUNT_DEFAULT,
  formatAkahuPlayerAmount,
} from "../akahu/copy";
import { PaymentMethod, PaymentRegion } from "./paymentMethod";
import type { FormFieldConfig } from "../../../mixitone/src/components/Forms/FormFieldsDefinition";

export const AkahuOneOffConfigSchema = z.object({
  amount: z.number(),
});

export type AkahuOneOffConfig = z.infer<typeof AkahuOneOffConfigSchema>;

export type AkahuOneOffConfigFieldOptions = {
  maxAmount?: number;
};

export class AkahuOneOff extends PaymentMethod<AkahuOneOffConfig> {
  id = "akahu-one-off";
  name = AKAHU_METHOD_NAME;
  supportedRegions: PaymentRegion[] = ["nz"];

  getConfigSchema() {
    return AkahuOneOffConfigSchema;
  }

  getConfigFields(options: AkahuOneOffConfigFieldOptions = {}): FormFieldConfig[] {
    const maxAmount = options.maxAmount ?? AKAHU_ONE_OFF_MAX_AMOUNT_DEFAULT;
    return [
      {
        type: "number",
        name: "amount",
        label: "Amount",
        required: true,
        helpText: AKAHU_AMOUNT_LIMIT_HELP(maxAmount),
        description: AKAHU_AMOUNT_LIMIT_HELP(maxAmount),
        prefix: "$",
        min: 0.01,
        max: maxAmount,
        step: 0.01,
        validate: (value: number) => {
          if (value == null || Number.isNaN(value)) return "This field is required";
          if (value < 0.01) return "Amount must be at least $0.01";
          if (value > maxAmount) return AKAHU_AMOUNT_OVER_LIMIT(maxAmount);
          return undefined;
        },
      },
    ];
  }

  getDescription(): string {
    return AKAHU_METHOD_DESCRIPTION;
  }

  getInstructions(config: AkahuOneOffConfig): string {
    return AKAHU_METHOD_INSTRUCTIONS(formatAkahuPlayerAmount(config.amount));
  }

  validateConfig(config: AkahuOneOffConfig): boolean {
    return AkahuOneOffConfigSchema.safeParse(config).success;
  }
}

export const createAkahuOneOffPaymentMethod = (): PaymentMethod<AkahuOneOffConfig> => {
  return new AkahuOneOff();
};
